import { FilterBar, FilterBarField } from "design-system/molecules/FilterBar"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { useCampaignsQuery } from "modules/campaigns/hooks/useCampaignsQuery"
import { useCanonicalTopicsQuery } from "modules/insights/hooks/useCanonicalTopicsQuery"
import { usePredefinedSegmentGroupsTranslation } from "modules/segments/hooks/usePredefinedSegmentGroupsTranslation"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { useSegmentGroupsQuery } from "modules/segments/hooks/useSegmentGroupsQuery"
import { ExportTable } from "modules/shared/components/ExportTable"
import { useFilterBarLabels } from "modules/shared/hooks/useFilterBarLabels"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import { useTranslation } from "react-i18next"
import { Route as SupportListRoute } from "routes/_base/_auth/support/list/route"
import {
  ChannelEnum,
  ClusterDomain,
  ClusterSegmentEnum,
  IssueStatusEnum,
  IssueUrgencyEnum,
  TicketStatusEnum,
} from "schema"

type Props = {
  showStatus: boolean
  showCanonicalTopic: boolean
  currentTab: string
  clusterDomain: ClusterDomain
}

type Search = ReturnType<typeof SupportListRoute.useSearch>
type SupportFilterBy = NonNullable<Search["filterBy"]>

const HUMAN_REPLY_VALUES = ["yes", "no"] as const

export function SupportFilter({
  showStatus,
  showCanonicalTopic,
  currentTab,
  clusterDomain,
}: Props) {
  const { t } = useTranslation()
  const search = SupportListRoute.useSearch()
  const navigate = SupportListRoute.useNavigate()
  const labels = useFilterBarLabels()

  const { data: canonicalTopics = [] } = useCanonicalTopicsQuery({
    query: {
      domain: clusterDomain,
      segment: ClusterSegmentEnum.all,
      withSnapshots: true,
    },
  })

  const { data: campaigns = [] } = useCampaignsQuery({
    query: {
      filterBy: { archived: false },
      total: 100,
      sortBy: { field: "name", order: "asc" },
    },
  })

  const { data: segmentGroups = [] } = useSegmentGroupsQuery()
  const { getSegmentGroupTranslation } = usePredefinedSegmentGroupsTranslation()
  const { getSegmentTranslation } = usePredefinedSegmentTranslations()

  function onChangeFilter(changes: Partial<SupportFilterBy>) {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        filterBy: { ...previous.filterBy, ...changes },
      }),
    })
  }

  function onClearFilters() {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        query: undefined,
        filterBy: { domain: previous.filterBy?.domain },
      }),
    })
  }

  function renderExportTable() {
    switch (currentTab) {
      case "tickets":
        return <ExportTable source="support.tickets" query={search} />
      case "questions":
        return <ExportTable source="support.questions" query={search} />
      case "newQuestions":
        return <ExportTable source="support.entities.questions" query={search} />
      case "actionRequests":
        return <ExportTable source="support.entities.actionRequests" query={search} />
      case "issues":
        return <ExportTable source="support.entities.issues" query={search} />
    }
  }

  const statusLabels = {
    Pending: t("support.tickets.status.pending"),
    Resolved: t("support.tickets.status.resolved"),
    PartiallyResolved: t("support.tickets.status.partiallyResolved"),
    Closed: t("support.tickets.status.closed"),
  } satisfies Record<keyof typeof TicketStatusEnum, string>

  const isTicketsOrIssuesTab = currentTab === "tickets" || currentTab === "issues"

  const fields: FilterBarField[] = [
    {
      key: "status",
      type: "multiSelect",
      label: t("support.filters.status"),
      condition: showStatus,
      value: search.filterBy?.status,
      onChange: (values) =>
        onChangeFilter({ status: pickValues(Object.values(TicketStatusEnum), values) }),
      options: Object.values(TicketStatusEnum).map((status) => ({
        value: status,
        label: statusLabels[status],
      })),
    },
    {
      key: "issueStatus",
      type: "multiSelect",
      label: t("support.filters.status"),
      condition: currentTab === "issues",
      value: search.filterBy?.issueStatus,
      onChange: (values) => onChangeFilter({ issueStatus: pickValues(IssueStatusEnum, values) }),
      options: IssueStatusEnum.map((status) => ({
        value: status,
        label: t(`support.entities.issueStatus.${status}`),
      })),
    },
    {
      key: "urgency",
      type: "select",
      label: t("support.filters.urgency"),
      condition: currentTab === "issues",
      value: search.filterBy?.urgency,
      onChange: (value) =>
        onChangeFilter({
          urgency: Object.values(IssueUrgencyEnum).find((urgency) => urgency === value),
        }),
      options: Object.values(IssueUrgencyEnum).map((urgency) => ({
        value: urgency,
        label: t(`support.entities.urgency.${urgency}`),
      })),
    },
    {
      key: "humanReply",
      type: "select",
      label: t("support.filters.humanReply"),
      condition: isTicketsOrIssuesTab,
      value: search.filterBy?.humanReply,
      onChange: (value) =>
        onChangeFilter({ humanReply: HUMAN_REPLY_VALUES.find((option) => option === value) }),
      options: [
        { value: "yes", label: t("support.filters.hasHumanReply") },
        { value: "no", label: t("support.filters.automated") },
      ],
    },
    {
      key: "unansweredCustomerReply",
      type: "select",
      label: t("support.filters.customerFollowUp"),
      condition: isTicketsOrIssuesTab,
      value: search.filterBy?.unansweredCustomerReply,
      onChange: (value) =>
        onChangeFilter({ unansweredCustomerReply: value === "yes" ? "yes" : undefined }),
      options: [{ value: "yes", label: t("support.filters.customerFollowUpNeedsReply") }],
    },
    {
      key: "campaignId",
      type: "multiSelect",
      label: t("support.filters.campaign"),
      value: search.filterBy?.campaignId,
      onChange: (values) => onChangeFilter({ campaignId: values }),
      options: campaigns.map((campaign) => {
        const hasName = campaign.name && campaign.name.length > 0

        return {
          value: campaign.id,
          label: hasName ? ensure(campaign.name) : t("campaigns.untitled"),
          className: cn(!hasName && "text-muted-foreground"),
        }
      }),
    },
    {
      key: "channel",
      type: "multiSelect",
      label: t("support.filters.channel"),
      value: search.filterBy?.channel,
      onChange: (values) => onChangeFilter({ channel: pickValues(SUPPORT_CHANNELS, values) }),
      options: SUPPORT_CHANNELS.map((channel) => ({
        value: channel,
        label: t(`campaigns.channel.${channel.toLowerCase()}.label`),
      })),
    },
    {
      key: "dateRange",
      type: "dateRange",
      label: t("shared.dateRange.placeholder"),
      value: search.filterBy?.dateRange,
      onChange: (dateRange) =>
        onChangeFilter({
          dateRange: dateRange?.from ? { from: dateRange.from, to: dateRange.to } : undefined,
        }),
    },
    {
      key: "segmentId",
      type: "multiSelect",
      label: t("support.filters.segment"),
      value: search.filterBy?.segmentId,
      onChange: (values) => onChangeFilter({ segmentId: values }),
      options: segmentGroups.flatMap((segmentGroup) =>
        segmentGroup.segments.map((segment) => ({
          value: segment.id,
          label: getSegmentTranslation(segment.name, segmentGroup),
          group: getSegmentGroupTranslation(segmentGroup) || segmentGroup.name,
        })),
      ),
    },
    {
      key: "canonicalId",
      type: "multiSelect",
      label: t("support.filters.canonicalTopic"),
      condition: showCanonicalTopic,
      value: search.filterBy?.canonicalId,
      onChange: (values) => onChangeFilter({ canonicalId: values }),
      options: canonicalTopics.map((canonicalTopic) => ({
        value: canonicalTopic.id,
        label: formatCanonicalTopicLabel(canonicalTopic.label),
      })),
    },
  ]

  return (
    <FilterBar
      className="w-full"
      fields={fields}
      labels={labels}
      onClearFilters={onClearFilters}
      searchQuery={search.query}
      onSearchQueryChange={(query) =>
        navigate({ search: (previous) => ({ ...previous, query: query || undefined, page: 1 }) })
      }
      searchPlaceholder={t("conversations.filters.search")}
      trailing={renderExportTable()}
    />
  )
}

// Email is a legacy channel superseded by Gmail and Outlook
const SUPPORT_CHANNELS = Object.values(ChannelEnum).filter(
  (channel) => channel !== ChannelEnum.Email,
)

function pickValues<T extends string>(allowed: readonly T[], values: string[] | undefined) {
  const picked = allowed.filter((value) => values?.includes(value))
  return picked.length > 0 ? picked : undefined
}
